import React, { useRef } from 'react';
import { defaultValueCtx, Editor, rootCtx } from '@milkdown/kit/core';
import { Milkdown, useEditor, MilkdownProvider } from '@milkdown/react';
import {
  ProsemirrorAdapterProvider,
} from '@prosemirror-adapter/react';
import { commonmark } from '@milkdown/kit/preset/commonmark';
import { listener, listenerCtx } from '@milkdown/kit/plugin/listener';
import { milkdownFountainPlugin } from '../../editor-plugins/milkdown-fountain-plugin';
import './styles.scss';

const markdown = `# Milkdown React Commonmark

> You're scared of a world where you're needed.

This is a demo for using Milkdown with **React**.`;

interface Props {
  content?: string;
  onChange?: (newContent: string) => void;
  className?: string;
  characters?: any[];
  locations?: any[];
}

export const BaseEditor: React.FC<Props> = ({
  content,
  onChange,
  className,
  characters,
  locations
}) => {
  // Use refs to avoid stale closures in the Milkdown editor callback.
  // The editor is created once and we need to always reference the latest
  // onChange and content without recreating the whole editor.
  const onChangeRef = useRef(onChange);
  const contentRef = useRef(content);

  onChangeRef.current = onChange;
  contentRef.current = content;

  useEditor((root) => {
    return Editor.make()
      .config((ctx) => {
        ctx.set(rootCtx, root);
        ctx.set(defaultValueCtx, contentRef.current || markdown);
        ctx.get(listenerCtx).markdownUpdated((ctx, markdown, prevMarkdown) => {
          if (markdown !== prevMarkdown) {
            // Ensure we only ever pass a string. Milkdown's listener should
            // provide a markdown string, but guard against edge cases where
            // a ProseMirror node object might leak through.
            const text = typeof markdown === 'string' ? markdown : String(markdown);
            onChangeRef.current?.(text);
          }
        });
      })
      .use(commonmark)
      .use(milkdownFountainPlugin as any)
      .use(listener);
  }, []);

  return (
    <div className={className || ''}>
      <Milkdown />
    </div>
  );
};

const MilkdownEditor: React.FC<Props> = ({
  content,
  onChange,
  className,
  characters,
  locations
}) => {
  return (
    <MilkdownProvider>
      <ProsemirrorAdapterProvider>
        <BaseEditor
          content={content}
          onChange={onChange}
          className={className}
          characters={characters}
          locations={locations}
        />
      </ProsemirrorAdapterProvider>
    </MilkdownProvider>
  );
};

export default MilkdownEditor;